import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { Sortable, SortableContent, SortableItem, SortableItemHandle } from "@/modern/components/ui/sortable";
import { Button } from "@/modern/components/ui/button";
import { Input } from "@/modern/components/ui/input";
import { Editable, EditableArea, EditablePreview, EditableInput, EditableTrigger } from "@/modern/components/ui/editable";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/modern/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/modern/components/ui/popover";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/modern/components/ui/collapsible";
import { GripVerticalIcon, TrashIcon, PlusIcon, Loader2, PencilIcon, Filter, ArrowUpDown, Group, ChevronDown, ChevronRight, X, Search } from "lucide-react";
import { cn } from "@/modern/utils";
import { debounce } from "@/other/utils/otherUtils";

// ==================== TYPES ====================

interface SwivuListItem {
  id?: string | number;
  [key: string]: any;
}

interface FilterOption {
  value: string;
  label: string;
}

interface FilterField<T = any> {
  field: string;
  label: string;
  options: FilterOption[];
  filterFn?: (item: T, selectedValues: string[]) => boolean;
}

interface SortField {
  field: string;
  label: string;
}

interface GroupField {
  field: string;
  label: string;
  getGroupLabel?: (value: any) => string;
}

interface FilterConfig<T = any> {
  fields: FilterField<T>[];
  onChange?: (filters: Record<string, string[]>) => void;
}

interface SortConfig {
  fields: SortField[];
  onChange?: (field: string, direction: "asc" | "desc") => void;
}

interface GroupConfig {
  fields: GroupField[];
  onChange?: (field: string | null) => void;
}

interface SearchConfig {
  fields: string[];
  placeholder?: string;
  debounceMs?: number;
  inputClassName?: string;
}

interface SwivuListConfig<T extends SwivuListItem> {
  items?: T[];
  onReorder?: (movedItem: T, beforeItem: T | null) => Promise<void> | void;
  searchConfig?: SearchConfig;
  filterConfig?: FilterConfig<T>;
  sortConfig?: SortConfig;
  groupConfig?: GroupConfig;
}

interface SwivuListContextValue<T extends SwivuListItem> {
  items: T[];
  setItems: (items: T[]) => void;
  config: SwivuListConfig<T>;
  // Search state
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  debouncedSearchQuery: string;
  // Filter/Sort/Group state
  filters: Record<string, string[]>;
  setFilters: (filters: Record<string, string[]>) => void;
  sortField: string | null;
  sortDirection: "asc" | "desc";
  setSorting: (field: string | null, direction: "asc" | "desc") => void;
  groupField: string | null;
  setGroupField: (field: string | null) => void;
  // Processed items
  processedItems: T[];
  groupedItems: Map<any, T[]> | null;
}

// ==================== CONTEXT ====================

const SwivuListContext = createContext<SwivuListContextValue<any> | null>(null);

const useSwivuListContext = <T extends SwivuListItem>() => {
  const context = useContext(SwivuListContext);
  if (!context) {
    throw new Error("SwivuList components must be used within SwivuList.Root");
  }
  return context as SwivuListContextValue<T>;
};

// ==================== HELPER FUNCTIONS ====================

function applySearch<T extends SwivuListItem>(
  items: T[],
  query: string,
  searchConfig?: SearchConfig
): T[] {
  if (!query || !searchConfig) return items;

  const lowerQuery = query.toLowerCase();
  return items.filter((item) => {
    return searchConfig.fields.some((field) => {
      const value = String(item[field] ?? "").toLowerCase();
      return value.includes(lowerQuery);
    });
  });
}

function applyFilters<T extends SwivuListItem>(
  items: T[],
  filters: Record<string, string[]>,
  filterConfig?: FilterConfig<T>
): T[] {
  return items.filter((item) => {
    return Object.entries(filters).every(([field, values]) => {
      if (values.length === 0) return true;

      // Check if there's a custom filter function
      const filterField = filterConfig?.fields.find(f => f.field === field);
      if (filterField?.filterFn) {
        return filterField.filterFn(item, values);
      }

      // Default filter: exact match
      const itemValue = String(item[field] ?? "");
      return values.includes(itemValue);
    });
  });
}

function applySorting<T extends SwivuListItem>(
  items: T[],
  field: string | null,
  direction: "asc" | "desc"
): T[] {
  if (!field) return items;

  return [...items].sort((a, b) => {
    const aVal = a[field];
    const bVal = b[field];

    if (aVal === bVal) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;

    const comparison = aVal < bVal ? -1 : 1;
    return direction === "asc" ? comparison : -comparison;
  });
}

function applyGrouping<T extends SwivuListItem>(
  items: T[],
  field: string | null
): Map<any, T[]> | null {
  if (!field) return null;

  const groups = new Map<any, T[]>();

  items.forEach((item) => {
    const groupValue = item[field] ?? null;
    if (!groups.has(groupValue)) {
      groups.set(groupValue, []);
    }
    groups.get(groupValue)!.push(item);
  });

  return groups;
}

// ==================== ROOT COMPONENT ====================

interface SwivuListRootProps<T extends SwivuListItem> extends SwivuListConfig<T> {
  children: React.ReactNode;
  className?: string;
}

function SwivuListRoot<T extends SwivuListItem>({
  children,
  items: initialItems = [],
  searchConfig,
  filterConfig,
  sortConfig,
  groupConfig,
  className,
  ...config
}: SwivuListRootProps<T>) {
  const [items, setItems] = useState<T[]>(initialItems);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [groupField, setGroupField] = useState<string | null>(null);

  useEffect(() => {
    setItems(initialItems);
  }, [initialItems]);

  // Debounce search query
  useEffect(() => {
    const debouncedFn = debounce((query: string) => {
      setDebouncedSearchQuery(query);
    }, searchConfig?.debounceMs || 300);

    debouncedFn(searchQuery);
  }, [searchQuery, searchConfig?.debounceMs]);

  const setSorting = useCallback((field: string | null, direction: "asc" | "desc") => {
    setSortField(field);
    setSortDirection(direction);
    if (sortConfig?.onChange && field) {
      sortConfig.onChange(field, direction);
    }
  }, [sortConfig]);

  const handleSetFilters = useCallback((newFilters: Record<string, string[]>) => {
    setFilters(newFilters);
    if (filterConfig?.onChange) {
      filterConfig.onChange(newFilters);
    }
  }, [filterConfig]);

  const handleSetGroupField = useCallback((field: string | null) => {
    setGroupField(field);
    if (groupConfig?.onChange) {
      groupConfig.onChange(field);
    }
  }, [groupConfig]);

  // Process items: search -> filter -> sort -> group
  const processedItems = useMemo(() => {
    let result = items;

    // Apply search
    result = applySearch(result, debouncedSearchQuery, searchConfig);

    // Apply filters
    result = applyFilters(result, filters, filterConfig);

    // Apply sorting
    result = applySorting(result, sortField, sortDirection);

    return result;
  }, [items, debouncedSearchQuery, searchConfig, filters, filterConfig, sortField, sortDirection]);

  const groupedItems = useMemo(() => {
    return applyGrouping(processedItems, groupField);
  }, [processedItems, groupField]);

  const contextValue: SwivuListContextValue<T> = {
    items,
    setItems,
    config: { ...config, searchConfig, filterConfig, sortConfig, groupConfig },
    searchQuery,
    setSearchQuery,
    debouncedSearchQuery,
    filters,
    setFilters: handleSetFilters,
    sortField,
    sortDirection,
    setSorting,
    groupField,
    setGroupField: handleSetGroupField,
    processedItems,
    groupedItems,
  };

  return (
    <SwivuListContext.Provider value={contextValue}>
      <div className={cn("space-y-4 flex-1 overflow-y-auto", className)}>{children}</div>
    </SwivuListContext.Provider>
  );
}

// ==================== TOOLBAR COMPONENT ====================

function SwivuListToolbar() {
  const { config, searchQuery, setSearchQuery, filters, setFilters, sortField, sortDirection, setSorting, groupField, setGroupField } = useSwivuListContext();
  const { searchConfig, filterConfig, sortConfig, groupConfig } = config;

  if (!searchConfig && !filterConfig && !sortConfig && !groupConfig) {
    return null;
  }

  const handleFilterChange = (field: string, value: string) => {
    const newFilters = { ...filters };
    if (value) {
      newFilters[field] = [value];
    } else {
      delete newFilters[field];
    }
    setFilters(newFilters);
  };

  const handleRemoveFilter = (field: string) => {
    const newFilters = { ...filters };
    delete newFilters[field];
    setFilters(newFilters);
  };

  const handleSortChange = (field: string) => {
    if (sortField === field) {
      // Toggle direction
      setSorting(field, sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSorting(field, "asc");
    }
  };

  const activeFilters = Object.entries(filters).filter(([_, values]) => values.length > 0);

  const getFilterLabel = (field: string, value: string) => {
    const filterField = filterConfig?.fields.find(f => f.field === field);
    if (!filterField) return value;

    const option = filterField.options.find(o => o.value === value);
    return option ? `${filterField.label}: ${option.label}` : value;
  };

  const getSortLabel = () => {
    if (!sortField) return "";
    const field = sortConfig?.fields.find(f => f.field === sortField);
    return field ? `${field.label} ${sortDirection === "asc" ? "↑" : "↓"}` : "";
  };

  const getGroupLabel = () => {
    if (!groupField) return "";
    const field = groupConfig?.fields.find(f => f.field === groupField);
    return field ? `Grouped by ${field.label}` : "";
  };

  const hasFilters = filterConfig || sortConfig || groupConfig;

  return (
    <div className="flex items-center justify-between gap-3 px-1 pt-1">
      {/* Left: Search Bar */}
      {searchConfig && (
        <div className="flex-1 max-w-xs">
          <div className="relative flex items-center">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={searchConfig.placeholder || "Search..."}
              className={cn("!h-8 w-full !pl-7 !pr-8 text-[11px] !py-0", searchConfig.inputClassName)}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-accent rounded-sm transition-colors"
                title="Clear search"
              >
                <X className="h-3 w-3 text-muted-foreground" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Right: Filter/Sort/Group */}
      {hasFilters && (
        <div className="flex flex-wrap items-center gap-2">
          {/* Filter Button + Badges */}
          {filterConfig && (
            <>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    title="Filter"
                  >
                    <Filter className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-0" align="start">
                  <div className="p-3 space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-xs">Filters</h4>
                      {activeFilters.length > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 text-[11px] px-2"
                          onClick={() => setFilters({})}
                        >
                          Clear all
                        </Button>
                      )}
                    </div>
                    {filterConfig.fields.map((filterField) => {
                      const currentValue = filters[filterField.field]?.[0];
                      const hasValue = !!currentValue;
                      return (
                        <div key={filterField.field} className="space-y-1.5">
                          <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                            {filterField.label}
                          </label>
                          <div className="relative">
                            <Select
                              value={currentValue}
                              onValueChange={(value) => handleFilterChange(filterField.field, value)}
                            >
                              <SelectTrigger className={cn("h-8 text-xs", hasValue && "pr-7")}>
                                <SelectValue placeholder="No filter" />
                              </SelectTrigger>
                              <SelectContent>
                                {filterField.options.map((option) => (
                                  <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {hasValue && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveFilter(filterField.field);
                                }}
                                className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 hover:bg-accent rounded-sm transition-colors z-10"
                                title="Clear filter"
                              >
                                <X className="h-3 w-3 text-muted-foreground" />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </PopoverContent>
              </Popover>

              {/* Filter Badges */}
              {activeFilters.map(([field, values]) => (
                <div
                  key={field}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-primary/10 text-primary rounded-md border border-primary/20"
                >
                  <span>{getFilterLabel(field, values[0])}</span>
                  <button
                    onClick={() => handleRemoveFilter(field)}
                    className="hover:bg-primary/20 rounded-sm p-0.5 transition-colors"
                    title="Remove filter"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </>
          )}

          {/* Sort Button + Badge */}
          {sortConfig && (
            <>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    title="Sort"
                  >
                    <ArrowUpDown className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-52 p-0" align="start">
                  <div className="p-2">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-xs">Sort By</h4>
                      {sortField && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 text-[11px] px-2"
                          onClick={() => setSorting(null, "asc")}
                        >
                          Clear
                        </Button>
                      )}
                    </div>
                    <div className="space-y-0.5">
                      {sortConfig.fields.map((field) => (
                        <Button
                          key={field.field}
                          variant={sortField === field.field ? "secondary" : "ghost"}
                          size="sm"
                          className="w-full justify-start h-8 text-xs px-2"
                          onClick={() => handleSortChange(field.field)}
                        >
                          <span className="flex-1 text-left">{field.label}</span>
                          {sortField === field.field && (
                            <span className="text-muted-foreground text-xs">
                              {sortDirection === "asc" ? "↑" : "↓"}
                            </span>
                          )}
                        </Button>
                      ))}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>

              {/* Sort Badge */}
              {sortField && (
                <div className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-500/10 text-blue-600 dark:text-blue-400 rounded-md border border-blue-500/20">
                  <span>{getSortLabel()}</span>
                  <button
                    onClick={() => setSorting(null, "asc")}
                    className="hover:bg-blue-500/20 rounded-sm p-0.5 transition-colors"
                    title="Remove sort"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
            </>
          )}

          {/* Group Button + Badge */}
          {groupConfig && (
            <>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    title="Group"
                  >
                    <Group className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-52 p-0" align="start">
                  <div className="p-2">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-xs">Group By</h4>
                      {groupField && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 text-[11px] px-2"
                          onClick={() => setGroupField(null)}
                        >
                          Clear
                        </Button>
                      )}
                    </div>
                    <div className="space-y-0.5">
                      {groupConfig.fields.map((field) => (
                        <Button
                          key={field.field}
                          variant={groupField === field.field ? "secondary" : "ghost"}
                          size="sm"
                          className="w-full justify-start h-8 text-xs px-2"
                          onClick={() => setGroupField(field.field)}
                        >
                          {field.label}
                        </Button>
                      ))}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>

              {/* Group Badge */}
              {groupField && (
                <div className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-purple-500/10 text-purple-600 dark:text-purple-400 rounded-md border border-purple-500/20">
                  <span>{getGroupLabel()}</span>
                  <button
                    onClick={() => setGroupField(null)}
                    className="hover:bg-purple-500/20 rounded-sm p-0.5 transition-colors"
                    title="Remove grouping"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ==================== LIST CONTENT ====================

interface SwivuListContentProps<T extends SwivuListItem> {
  children?: (item: T) => React.ReactNode;
  className?: string;
  emptyState?: React.ReactNode;
  emptyMessage?: string;
}

function SwivuListContent<T extends SwivuListItem>({
  children,
  className,
  emptyState,
  emptyMessage = "No items yet.",
}: SwivuListContentProps<T>) {
  const { config, processedItems, groupedItems, groupField, debouncedSearchQuery, filters } = useSwivuListContext<T>();
  const { onReorder, groupConfig } = config;
  const [openGroups, setOpenGroups] = useState<Set<any>>(new Set());

  const handleMove = useCallback(
    (event: { activeIndex: number; overIndex: number }) => {
      const { activeIndex, overIndex } = event;

      if (!onReorder) return;

      const movedItem = processedItems[activeIndex];

      // When moving down (overIndex > activeIndex), we need to account for the shift
      // that happens when the item is removed from its original position.
      // We want to insert before the item at overIndex + 1.
      // When moving up, we insert before the item at overIndex.
      let beforeItem: T | null;

      if (overIndex > activeIndex) {
        // Moving down: insert before the item after overIndex
        beforeItem = overIndex + 1 < processedItems.length ? processedItems[overIndex + 1] : null;
      } else {
        // Moving up: insert before the item at overIndex
        beforeItem = processedItems[overIndex];
      }

      onReorder(movedItem, beforeItem);
    },
    [processedItems, onReorder]
  );

  const toggleGroup = useCallback((groupValue: any) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupValue)) {
        next.delete(groupValue);
      } else {
        next.add(groupValue);
      }
      return next;
    });
  }, []);

  // Open all groups by default
  useEffect(() => {
    if (groupedItems) {
      setOpenGroups(new Set(Array.from(groupedItems.keys())));
    }
  }, [groupedItems]);

  if (processedItems.length === 0) {
    // Check if any filters/search are active (not sort/group, as those don't filter data)
    const hasActiveSearch = !!debouncedSearchQuery;
    const hasActiveFilters = Object.values(filters).some((values) => values.length > 0);
    const isFiltered = hasActiveSearch || hasActiveFilters;

    // Show different message if filtered
    const displayMessage = isFiltered ? "No results" : emptyMessage;

    return (
      emptyState || (
        <div className="flex items-center justify-center min-h-16">
          <p className="text-sm text-muted-foreground">{displayMessage}</p>
        </div>
      )
    );
  }

  if (!children) {
    throw new Error("SwivuList.Content requires children as a render function");
  }

  // Grouped rendering
  if (groupedItems && groupField) {
    const getGroupLabel = (groupValue: any) => {
      if (groupValue === null) return "Ungrouped";

      const groupFieldConfig = groupConfig?.fields.find(f => f.field === groupField);
      if (groupFieldConfig?.getGroupLabel) {
        return groupFieldConfig.getGroupLabel(groupValue);
      }

      return String(groupValue);
    };

    return (
      <div className={cn("space-y-4", className)}>
        {Array.from(groupedItems.entries()).map(([groupValue, groupItems]) => {
          const isOpen = openGroups.has(groupValue);

          return (
            <Collapsible key={String(groupValue)} open={isOpen} onOpenChange={() => toggleGroup(groupValue)}>
              <div className="border rounded-lg">
                <CollapsibleTrigger asChild>
                  <button className="flex items-center w-full px-3 py-2 text-sm font-medium hover:bg-accent/50 transition-colors">
                    {isOpen ? (
                      <ChevronDown className="h-4 w-4 mr-2" />
                    ) : (
                      <ChevronRight className="h-4 w-4 mr-2" />
                    )}
                    {getGroupLabel(groupValue)}
                    <span className="ml-2 px-2 py-0.5 text-xs bg-muted rounded">
                      {groupItems.length}
                    </span>
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="p-2 space-y-4">
                    {groupItems.map((item) => children(item))}
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>
          );
        })}
      </div>
    );
  }

  // Regular rendering - with or without sorting
  if (onReorder) {
    // Sortable rendering when onReorder is provided
    return (
      // @ts-expect-error - Sortable component has complex typing with getItemValue
      <Sortable
        value={processedItems}
        onMove={handleMove}
        getItemValue={(item) => item.id}
      >
        <SortableContent className={cn("space-y-4", className)}>
          {processedItems.map((item) => children(item))}
        </SortableContent>
      </Sortable>
    );
  }

  // Non-sortable rendering
  return (
    <div className={cn("space-y-4", className)}>
      {processedItems.map((item) => children(item))}
    </div>
  );
}

// ==================== ITEM COMPONENT ====================

interface SwivuListItemProps {
  value: string | number;
  children: React.ReactNode;
  className?: string;
}

function SwivuListItem({ value, children, className }: SwivuListItemProps) {
  const { groupedItems, config } = useSwivuListContext();
  const { onReorder } = config;

  // If grouped or no reorder handler, don't use SortableItem
  if (groupedItems || !onReorder) {
    return (
      <div key={value} className={cn("flex items-center gap-2 p-2.5 group bg-card rounded-lg border border-border/40", className)}>
        {children}
      </div>
    );
  }

  // Regular sortable item
  return (
    <SortableItem key={value} value={value}>
      <div className={cn("flex items-center gap-2 p-2.5 group bg-card rounded-lg border border-border/40", className)}>
        {children}
      </div>
    </SortableItem>
  );
}

// ==================== ITEM HANDLE ====================

interface SwivuListItemHandleProps {
  value?: string | number;
  disabled?: boolean;
  className?: string;
  showOnHover?: boolean;
}

function SwivuListItemHandle({ value, disabled = false, className, showOnHover = false }: SwivuListItemHandleProps) {
  const { groupedItems, config } = useSwivuListContext();
  const { onReorder } = config;
  const SHOW_ON_HOVER_CLS = "opacity-0 group-hover:opacity-100 transition-opacity";

  // Don't show drag handle when grouped or no reorder handler
  if (groupedItems || !onReorder) {
    return null;
  }

  return (
    <SortableItemHandle disabled={disabled} value={value}>
      <Button
        variant="ghost"
        size="icon"
        className={cn("h-8 w-8 p-0", showOnHover ? SHOW_ON_HOVER_CLS : "", className)}
        disabled={disabled}
      >
        <GripVerticalIcon className="size-4 text-muted-foreground" />
      </Button>
    </SortableItemHandle>
  );
}

// ==================== TEXT FIELD ====================

interface SwivuListTextFieldProps<T extends SwivuListItem> {
  item: T;
  value: string;
  onUpdate: (item: T, newValue: string) => void | Promise<void> | boolean | Promise<boolean>;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  type?: "text" | "number";
}

function SwivuListTextField<T extends SwivuListItem>({
  item,
  value,
  onUpdate,
  placeholder = "Enter value...",
  disabled = false,
  className,
  type = "text",
}: SwivuListTextFieldProps<T>) {
  const [currentValue, setCurrentValue] = useState(value);

  // Sync with prop changes
  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  const handleSubmit = useCallback(
    async (newValue: string) => {
      if (newValue === value) {
        return;
      }

      const result = await onUpdate(item, newValue.trim());

      // If validation fails, revert to original value
      if (result === false) {
        setCurrentValue(value);
      } else {
        // Success - update to new value
        setCurrentValue(newValue.trim());
      }
    },
    [item, value, onUpdate]
  );

  return (
    <Editable
      value={currentValue}
      onValueChange={setCurrentValue}
      onSubmit={handleSubmit}
      placeholder={placeholder}
      triggerMode="click"
      className={cn("flex-row items-center group/editable-area", className)}
      disabled={disabled}
    >
      <EditableArea>
        <EditablePreview className="text-sm font-medium px-2 py-1 border-0 truncate min-w-0 flex-1" />
        <EditableInput type={type} className="text-sm font-medium h-8" />
      </EditableArea>
      <EditableTrigger asChild>
        {!disabled && <Button
          size="icon"
          variant="ghost"
          className="h-6 w-6 shrink-0 opacity-0 group-hover/editable-area:opacity-100 transition-opacity"
        >
          <PencilIcon className="size-2" />
        </Button>}
      </EditableTrigger>
    </Editable>
  );
}

// ==================== ITEM ACTIONS ====================

interface SwivuListItemActionsProps {
  onEdit?: () => void;
  onDelete?: () => void;
  className?: string;
  disabled?: boolean;
  showOnHover?: boolean;
}

function SwivuListItemActions({ onEdit, onDelete, className, disabled = false, showOnHover = false }: SwivuListItemActionsProps) {
  const SHOW_ON_HOVER_CLS = "opacity-0 group-hover:opacity-100 transition-opacity";

  return (
    <div className={cn("flex items-center gap-0.5", className)}>
      {onEdit && (
        <Button
          variant="ghost"
          size="icon"
          className={cn("h-8 w-8 p-0", showOnHover ? SHOW_ON_HOVER_CLS : "")}
          disabled={disabled}
          onClick={onEdit}
        >
          <PencilIcon className="size-4 text-muted-foreground" />
        </Button>
      )}
      {onDelete && (
        <Button
          variant="ghost"
          size="icon"
          className={cn("h-8 w-8 p-0", showOnHover ? SHOW_ON_HOVER_CLS : "")}
          disabled={disabled}
          onClick={onDelete}
        >
          <TrashIcon className="size-4 text-muted-foreground" />
        </Button>
      )}
    </div>
  );
}

// ==================== HEADER ====================

interface SwivuListHeaderProps {
  children: React.ReactNode;
  className?: string;
}

function SwivuListHeader({ children, className }: SwivuListHeaderProps) {
  return (
    <div className={cn("flex items-center gap-2 px-2.5 py-2 mb-2 bg-muted/30 rounded-md border border-border/40", className)}>
      {children}
    </div>
  );
}

// ==================== HEADER ITEM ====================

interface SwivuListHeaderItemProps {
  children: React.ReactNode;
  className?: string;
  width?: string;
  minWidth?: string;
  flex?: boolean;
  subtitle?: string;
}

function SwivuListHeaderItem({ children, className, width, minWidth, flex = false, subtitle }: SwivuListHeaderItemProps) {
  const baseClasses = flex ? "flex-1" : "flex-shrink-0";
  const style: React.CSSProperties = {};

  if (width) style.width = width;
  if (minWidth) style.minWidth = minWidth;

  return (
    <div className={cn(baseClasses, className)} style={style}>
      {subtitle ? (
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            {children}
          </span>
          <span className="text-[10px] text-muted-foreground/60">
            {subtitle}
          </span>
        </div>
      ) : (
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          {children}
        </span>
      )}
    </div>
  );
}

// ==================== HEADER HANDLE SPACE ====================

interface SwivuListHeaderHandleSpaceProps {
  className?: string;
}

function SwivuListHeaderHandleSpace({ className }: SwivuListHeaderHandleSpaceProps) {
  return <div className={cn("w-8 flex-shrink-0", className)} />;
}

// ==================== ADD FORM ====================

interface SwivuListQuickAddProps {
  onAdd: (value: string) => Promise<void> | void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  inputClassName?: string;
}

function SwivuListQuickAdd({ onAdd, placeholder = "Add new item...", disabled = false, className, inputClassName }: SwivuListQuickAddProps) {
  const [value, setValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!value.trim()) return;

    setIsLoading(true);
    try {
      await onAdd(value.trim());
      setValue("");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form className={cn("flex gap-2 sticky bottom-0 px-1 py-1", className)} onSubmit={handleSubmit}>
      <Input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={placeholder}
        className={cn("flex-1", inputClassName)}
        disabled={disabled || isLoading}
      />
      <Button
        disabled={disabled || isLoading}
        type="submit"
        size="icon"
        className="rounded-md h-swivu-input min-w-11"
      >
        {isLoading ? <Loader2 className="size-4 animate-spin" /> : <PlusIcon className="h-10 w-10" />}
      </Button>
    </form>
  );
}

// ==================== EXPORTS ====================

export const SwivuList = {
  Root: SwivuListRoot,
  Toolbar: SwivuListToolbar,
  Header: SwivuListHeader,
  HeaderItem: SwivuListHeaderItem,
  HeaderHandleSpace: SwivuListHeaderHandleSpace,
  Content: SwivuListContent,
  Item: SwivuListItem,
  ItemHandle: SwivuListItemHandle,
  TextField: SwivuListTextField,
  ItemActions: SwivuListItemActions,
  QuickAdd: SwivuListQuickAdd,
};

export type { SwivuListItem, SwivuListConfig, SearchConfig, FilterConfig, SortConfig, GroupConfig };
export { useSwivuListContext };
